import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, X, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Sticky banner that surfaces a quiet, professional notice when the
 * signed-in user's organisation is in grace mode or has been suspended
 * for subscription expiry.
 *
 * Reads /api/readiness/subscription-state (the same endpoint the grace
 * middleware uses to short-circuit writes with HTTP 402). Renders only
 * when state ∈ {expiring_soon, in_grace, suspended}; silent otherwise.
 *
 * Sessions can dismiss the banner — the dismissal is stored in
 * localStorage keyed by both the org id and the state so a state change
 * (e.g. moving from expiring_soon → in_grace) re-surfaces the banner.
 */
const ROLE_CAN_RENEW = new Set([
  "super_admin", "org_admin", "event_manager", "school_admin",
]);

const GRACE_PERIOD_DAYS = 14;

const STATE_PRESENTATION = {
  expiring_soon: {
    tone: "amber",
    title: (days) =>
      days === 0
        ? "Your subscription expires today."
        : `Your subscription expires in ${days} day${days === 1 ? "" : "s"}.`,
    body:
      "Renew before the grace period to keep events, invoices and live committees fully active.",
    cta: "Renew now",
  },
  in_grace: {
    tone: "amber",
    title: (days) => {
      // days is negative (expired). daysLeftInGrace counts down to suspension.
      const daysLeftInGrace = Math.max(GRACE_PERIOD_DAYS + days, 0);
      if (daysLeftInGrace <= 1) {
        return "Your organisation is in grace mode — renew today to avoid suspension.";
      }
      return `Your organisation is in grace mode — ${daysLeftInGrace} day${
        daysLeftInGrace === 1 ? "" : "s"
      } left to renew before suspension.`;
    },
    body:
      "Some actions may be read-only until subscription renewal. You can still view your data and contact billing.",
    cta: "Renew now",
  },
  suspended: {
    tone: "rose",
    title: () => "Your subscription is currently suspended.",
    body:
      "Writes are paused while we wait for renewal. Reach out to billing if this looks wrong — we'll get you back online.",
    cta: "Contact billing",
  },
};

const TONE_STYLES = {
  amber: {
    bar: "bg-amber-50 border-amber-200 text-amber-900",
    icon: "text-amber-600",
    cta: "bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F]",
  },
  rose: {
    bar: "bg-rose-50 border-rose-200 text-rose-900",
    icon: "text-rose-600",
    cta: "bg-rose-600 text-white hover:bg-rose-700",
  },
};

export default function GraceModeBanner() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || !user.id) return;
    let cancelled = false;
    api
      .get("/readiness/subscription-state")
      .then((r) => {
        if (cancelled) return;
        setState(r.data || null);
      })
      .catch(() => {
        // Silent: banner is purely informational; never block UI on it.
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user || !state) return null;
  const flavour = STATE_PRESENTATION[state.state];
  if (!flavour) return null;

  const dismissKey = `diplomexa_grace_dismissed_${state.state}_${state.expires_at || ""}`;
  if (!dismissed && typeof window !== "undefined") {
    if (window.localStorage.getItem(dismissKey) === "1") {
      // Persisted dismissal for this exact state — keep banner hidden until
      // the state changes (the key includes both state + expires_at).
      return null;
    }
  }
  if (dismissed) return null;

  const tone = TONE_STYLES[flavour.tone] || TONE_STYLES.amber;
  const canRenew = ROLE_CAN_RENEW.has(user.role);
  const days = Number.isFinite(state.days_to_expiry) ? state.days_to_expiry : 0;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(dismissKey, "1");
    } catch {
      /* noop */
    }
  };

  return (
    <div
      data-testid="grace-mode-banner"
      data-state={state.state}
      className={`sticky top-0 z-40 border-b ${tone.bar} print:hidden`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-start sm:items-center gap-3">
        <AlertCircle className={`h-4 w-4 mt-0.5 sm:mt-0 shrink-0 ${tone.icon}`} />
        <div className="flex-1 text-xs sm:text-sm leading-snug">
          <strong className="font-semibold">{flavour.title(days)}</strong>{" "}
          <span className="opacity-90">{flavour.body}</span>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          {canRenew && (
            <Link
              to="/pricing"
              data-testid="grace-mode-renew-cta"
              className={`text-xs font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1 ${tone.cta}`}
            >
              {flavour.cta} <ChevronRight className="h-3 w-3" />
            </Link>
          )}
          <Link
            to="/dashboard/billing"
            data-testid="grace-mode-billing-link"
            className="text-xs font-semibold px-3 py-1.5 rounded-md border border-current/30 hover:bg-white/40 inline-flex items-center gap-1"
          >
            View billing
          </Link>
          <button
            type="button"
            onClick={dismiss}
            data-testid="grace-mode-dismiss"
            aria-label="Dismiss banner"
            className="p-1 rounded hover:bg-white/40"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss banner"
          className="sm:hidden p-1 rounded hover:bg-white/40"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
